// Copyright ©️ 2026 eVolve MEP, LLC

import { Text } from '@mantine/core';
import { notifications } from '@mantine/notifications';

import { VariableTrackingModal } from 'components/Mantine/Modals/VariableTrackingModal';

import type { CustomStatus, CustomStatusEntityName } from './types';
import { useCustomStatuses } from './useCustomStatuses';

type Props<TEntity extends CustomStatusEntityName> = {
  customStatusTypeName: TEntity;
  /** Status to confirm deletion of. The modal is hidden when `null`. */
  status: CustomStatus<TEntity> | null;
  onClose: () => void;
};

/** Confirmation modal for deleting a custom status. */
export const DeleteStatusModal = <TEntity extends CustomStatusEntityName>({
  customStatusTypeName,
  status,
  onClose,
}: Props<TEntity>) => {
  const { pluralName, deleteStatus } = useCustomStatuses(customStatusTypeName);
  return (
    <VariableTrackingModal
      title="Delete status"
      size="sm"
      variable={status}
      onClose={onClose}
      confirmationText="Delete"
      onConfirm={async (deletingStatus) => {
        await deleteStatus(deletingStatus.statusId);
        notifications.show({
          title: 'Successfully deleted status',
          message: `Status ${deletingStatus.name} deleted.`,
          color: 'green',
        });
      }}
    >
      {(deletingStatus) => (
        <>
          <Text>
            Delete status <b>{deletingStatus.name}</b>?
          </Text>
          <Text>This will remove the status from all {pluralName}.</Text>
        </>
      )}
    </VariableTrackingModal>
  );
};
